/**
 * A3 (2026-06-27) — Customer Change Password (web).
 *
 * Reached in two situations:
 *   (a) After an owner/SA reset flipped `password_is_temporary=true`.
 *       The AuthStore's gated router sends the user here on login /
 *       bootstrap / 403.
 *   (b) Future voluntary password change — not wired yet; this screen
 *       is reachable only via the gated router today.
 *
 * Hard rules:
 *   - Prevent navigation away while `password_is_temporary=true`
 *     (browser back is intercepted via popstate).
 *   - On submit success → refresh /api/auth/me → if profile is still
 *     incomplete, go to /customer/complete-profile, else
 *     /customer/home.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle, Lock } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuthStore } from '@/lib/auth';

function validateNewPassword(pwd) {
  if (pwd.length < 8) return 'New password must be at least 8 characters.';
  if (!/[A-Za-z]/.test(pwd)) return 'New password must contain a letter.';
  if (!/\d/.test(pwd)) return 'New password must contain a digit.';
  return null;
}

export default function CustomerChangePasswordPage() {
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const refreshUser = useAuthStore((s) => s.refreshUser);
  const signOut = useAuthStore((s) => s.signOut);

  const [oldPwd, setOldPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [newPwdConfirm, setNewPwdConfirm] = useState('');
  const [err, setErr] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const isForced = Boolean(user?.password_is_temporary);

  // Lock the back button while forced.
  useEffect(() => {
    if (!isForced) return undefined;
    window.history.pushState({ gated: 'change-password' }, '', window.location.href);
    const onPop = () => {
      window.history.pushState({ gated: 'change-password' }, '', window.location.href);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [isForced]);

  useEffect(() => {
    if (!token) navigate('/customer/login', { replace: true });
  }, [token, navigate]);

  const canSubmit =
    !submitting &&
    oldPwd.length > 0 &&
    newPwd.length >= 8 &&
    newPwdConfirm.length >= 8;

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setErr(null);
      const pwdErr = validateNewPassword(newPwd);
      if (pwdErr) {
        setErr({ title: 'Password too weak', message: pwdErr });
        return;
      }
      if (newPwd !== newPwdConfirm) {
        setErr({ title: "Passwords don't match", message: 'Please re-enter the same new password.' });
        return;
      }
      if (newPwd === oldPwd) {
        setErr({ title: 'Same password', message: 'New password must differ from the current one.' });
        return;
      }
      setSubmitting(true);
      try {
        await api.changePassword(token, {
          old_password:     oldPwd,
          new_password:     newPwd,
          password_confirm: newPwdConfirm,
        });
        const me = await refreshUser();
        if (me?.role === 'customer' && !me?.profile_completed) {
          navigate('/customer/complete-profile', { replace: true });
        } else {
          navigate('/customer/home', { replace: true });
        }
      } catch (e2) {
        const code = e2?.code || null;
        const detail = e2?.detail || '';
        const friendly =
          code === 'invalid_credentials' || /current password is incorrect/i.test(detail)
            ? 'Current password is incorrect.'
            : code === 'password_too_weak' || /password must be/i.test(detail)
            ? 'New password must be ≥8 chars with a letter and a digit.'
            : code === 'password_confirm_mismatch'
            ? 'Confirmation does not match.'
            : detail || 'Could not change password. Please try again.';
        setErr({ title: 'Change password failed', message: friendly });
      } finally {
        setSubmitting(false);
      }
    },
    [newPwd, newPwdConfirm, oldPwd, token, refreshUser, navigate]
  );

  return (
    <div
      className="min-h-screen bg-[#0A0A0A] text-white flex flex-col items-center justify-center px-4 py-10"
      data-testid="customer-change-password-page"
    >
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-3 mb-10 justify-center">
          <img src="/onestamp-logo.png" alt="OneStamp" className="w-12 h-12 rounded-2xl" />
          <div className="text-xs text-gold uppercase tracking-widest font-semibold">
            OneStamp
          </div>
        </div>

        <div className="rounded-3xl bg-[#15151B] border border-white/8 p-7 shadow-2xl">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-xl bg-gold/10 border border-gold/25 flex items-center justify-center">
              <Lock size={16} className="text-gold" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-semibold leading-none">
                {isForced ? 'Set a new password' : 'Change your password'}
              </h1>
            </div>
          </div>
          <p className="text-sm text-white/55 mb-6">
            {isForced
              ? 'Your account is using a temporary password. Please set a new one before continuing.'
              : 'Use at least 8 characters with a letter and a digit.'}
          </p>

          {err ? (
            <div
              className="mb-5 px-3 py-2.5 rounded-xl bg-rose-500/8 border border-rose-400/25 flex items-start gap-2 text-xs"
              data-testid="customer-change-password-error"
              role="alert"
            >
              <AlertCircle size={14} className="text-rose-300 mt-0.5 shrink-0" />
              <div>
                <div className="font-medium text-rose-200">{err.title}</div>
                <div className="text-rose-200/80 mt-0.5">{err.message}</div>
              </div>
            </div>
          ) : null}

          <form
            onSubmit={onSubmit}
            className="space-y-4"
            data-testid="customer-change-password-form"
            noValidate
          >
            <div>
              <label className="block text-xs font-mono text-white/55 mb-1.5 uppercase tracking-wider">
                {isForced ? 'Temporary password' : 'Current password'}
              </label>
              <input
                type="password"
                autoComplete="current-password"
                value={oldPwd}
                onChange={(e) => setOldPwd(e.target.value)}
                data-testid="customer-change-password-old"
                className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-gold/40 focus:bg-black/40 transition-colors"
                placeholder="••••••••"
              />
            </div>
            <div>
              <label className="block text-xs font-mono text-white/55 mb-1.5 uppercase tracking-wider">
                New password
              </label>
              <input
                type="password"
                autoComplete="new-password"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                data-testid="customer-change-password-new"
                className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-gold/40 focus:bg-black/40 transition-colors"
                placeholder="At least 8 chars, 1 letter, 1 digit"
              />
            </div>
            <div>
              <label className="block text-xs font-mono text-white/55 mb-1.5 uppercase tracking-wider">
                Confirm new password
              </label>
              <input
                type="password"
                autoComplete="new-password"
                value={newPwdConfirm}
                onChange={(e) => setNewPwdConfirm(e.target.value)}
                data-testid="customer-change-password-confirm"
                className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-gold/40 focus:bg-black/40 transition-colors"
                placeholder="Re-enter new password"
              />
            </div>

            <button
              type="submit"
              disabled={!canSubmit}
              data-testid="customer-change-password-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-gold text-black font-semibold rounded-xl py-2.5 text-sm hover:bg-gold-light disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-glow-gold-sm"
            >
              {submitting ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Updating…
                </>
              ) : (
                'Update password'
              )}
            </button>
          </form>

          <div className="mt-6 text-center text-[11px] text-white/35">
            <button
              type="button"
              onClick={() => {
                signOut();
                navigate('/customer/login', { replace: true });
              }}
              data-testid="customer-change-password-signout"
              className="hover:text-white/70 transition-colors"
            >
              Not now — sign out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
